import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Heart, Package, Palette, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import ProductCard from '../components/ProductCard';
import CategoryCard from '../components/CategoryCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Hero carousel images - diverse handcrafted products
  const heroSlides = [
    {
      image: "https://images.unsplash.com/photo-1759420303135-0224b8d9b798?w=1920&q=85",
      title: "Handcrafted Gift Bouquets",
      subtitle: "Beautiful arrangements for every occasion"
    },
    {
      image: "https://images.unsplash.com/photo-1697719449601-ca80107f3e61?w=1920&q=85",
      title: "Premium Gift Packaging",
      subtitle: "Elegant boxes with artisan touches"
    },
    {
      image: "https://images.unsplash.com/photo-1584126467121-0db1e6bb47a7?w=1920&q=85",
      title: "Wedding Trousseau",
      subtitle: "Exquisite packaging for your special day"
    },
    {
      image: "https://images.unsplash.com/photo-1609687772823-8a66791d82cc?w=1920&q=85",
      title: "Artisan Home Decor",
      subtitle: "Unique pieces crafted with love"
    },
    {
      image: "https://images.unsplash.com/photo-1576579310188-eef1e26e417a?w=1920&q=85",
      title: "Festival Gift Hampers",
      subtitle: "Perfect for corporate & personal gifting"
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, prodRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/products?featured=true&limit=8`)
        ]);
        setCategories(catRes.data);
        setFeaturedProducts(prodRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  const features = [
    { icon: Heart, title: 'Made with Love', description: 'Each piece is crafted with passion and attention to detail' },
    { icon: Palette, title: 'Fully Customizable', description: 'Personalize any product to match your vision' },
    { icon: Package, title: 'Premium Packaging', description: 'Beautiful gift-ready packaging included' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with Carousel */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        {/* Background Images Carousel */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="absolute inset-0 z-0"
          >
            <img
              src={heroSlides[currentSlide].image}
              alt={heroSlides[currentSlide].title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-charcoal/90 via-charcoal/70 to-charcoal/30" />
          </motion.div>
        </AnimatePresence>

        {/* Carousel Navigation */}
        <button
          onClick={prevSlide}
          className="absolute left-4 md:left-8 z-20 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          data-testid="hero-prev"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 md:right-8 z-20 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          data-testid="hero-next"
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${currentSlide === index
                  ? 'bg-white w-8'
                  : 'bg-white/50 hover:bg-white/70'
                }`}
              data-testid={`hero-dot-${index}`}
            />
          ))}
        </div>

        {/* Hero Content */}
        <div className="container-handcraft relative z-10 py-20">
          <div className="max-w-2xl">
            <motion.p
              key={`accent-${currentSlide}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-handwritten text-3xl md:text-4xl text-accent mb-4"
            >
              Welcome to
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="hero-title font-serif text-white mb-4"
            >
              YM's Creation
            </motion.h1>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4 }}
              >
                <p className="text-2xl md:text-3xl font-serif text-white/90 mb-2">
                  {heroSlides[currentSlide].title}
                </p>
                <p className="text-lg text-white/70 mb-8">
                  {heroSlides[currentSlide].subtitle}
                </p>
              </motion.div>
            </AnimatePresence>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-white/80 mb-8 max-w-lg"
            >
              Discover unique handcrafted treasures — from aromatic candles to custom portraits,
              elegant trousseau packaging to corporate gift hampers. Each piece tells a story of artistry and love.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap gap-4"
            >
              <Link to="/shop">
                <Button
                  className="btn-handcraft bg-accent text-white hover:bg-accent/90"
                  data-testid="hero-shop-now"
                >
                  Shop Now
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/portfolio">
                <Button
                  variant="outline"
                  className="btn-handcraft border-white text-white hover:bg-white hover:text-charcoal"
                  data-testid="hero-view-work"
                >
                  View Our Work
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Floating Product Categories Preview */}
        <div className="hidden lg:block absolute right-12 top-1/2 -translate-y-1/2 z-10">
          <div className="grid grid-cols-2 gap-3">
            {['Candles', 'Gifts', 'Trousseau', 'Portraits'].map((item, index) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                className="w-24 h-24 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center"
              >
                <span className="text-white text-xs font-medium text-center px-2">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Category Showcase Strip */}
      <section className="bg-charcoal py-4 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(2)].map((_, setIndex) => (
            <div key={setIndex} className="flex">
              {['Candles', 'Trousseau Packing', 'Cake Toppers', 'Photo Frames', 'Gift Bouquets', 'Portrait Paintings', 'Business Stickers', 'Festival Hampers'].map((cat, index) => (
                <span key={`${setIndex}-${index}`} className="mx-8 text-white/60 text-sm uppercase tracking-widest flex items-center">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent mr-3" />
                  {cat}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="section-spacing bg-white">
        <div className="container-handcraft">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-7 h-7 text-accent" />
                </div>
                <h3 className="font-serif text-xl mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="section-spacing">
        <div className="container-handcraft">
          <div className="text-center mb-12">
            <p className="font-handwritten text-2xl text-accent mb-2">Explore</p>
            <h2 className="font-serif text-3xl md:text-4xl text-charcoal">Our Collections</h2>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="aspect-square rounded-2xl skeleton" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {categories.slice(0, 8).map((category, index) => (
                <CategoryCard key={category.id} category={category} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Products */}
      <section className="section-spacing bg-white">
        <div className="container-handcraft">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-4">
            <div>
              <p className="font-handwritten text-2xl text-accent mb-2">Handpicked</p>
              <h2 className="font-serif text-3xl md:text-4xl text-charcoal">Featured Products</h2>
            </div>
            <Link to="/shop" className="underline-animation text-sm uppercase tracking-wider font-medium">
              View All Products
            </Link>
          </div>

          {loading ? (
            <div className="product-grid">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="space-y-4">
                  <div className="aspect-square rounded-2xl skeleton" />
                  <div className="h-6 w-3/4 skeleton rounded" />
                  <div className="h-4 w-1/2 skeleton rounded" />
                </div>
              ))}
            </div>
          ) : (
            <div className="product-grid">
              {featuredProducts.slice(0, 8).map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Custom Order CTA */}
      <section className="section-spacing bg-gradient-to-br from-charcoal to-charcoal/90 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-accent rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-sage rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        </div>
        <div className="container-handcraft relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-handwritten text-3xl text-accent mb-4">Something Special?</p>
              <h2 className="font-serif text-3xl md:text-5xl mb-6 leading-tight">
                Create Your Own Custom Masterpiece
              </h2>
              <p className="text-white/70 mb-8 text-lg">
                Have a unique vision? We love bringing custom ideas to life. From personalized gifts to corporate bulk orders,
                let's create something extraordinary together.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/shop">
                  <Button
                    className="btn-handcraft bg-accent text-white hover:bg-accent/90"
                    data-testid="custom-order-cta"
                  >
                    Start Your Custom Order
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    variant="outline"
                    className="btn-handcraft border-white/30 text-white hover:bg-white/10"
                  >
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <img
                  src="https://images.unsplash.com/photo-1576926241721-4b830531f667?w=400&q=80"
                  alt="Custom Portrait"
                  className="rounded-2xl w-full aspect-square object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1731763905104-c02e150ad178?w=400&q=80"
                  alt="Trousseau"
                  className="rounded-2xl w-full aspect-[4/3] object-cover"
                />
              </div>
              <div className="space-y-4 pt-8">
                <img
                  src="https://images.unsplash.com/photo-1626037235530-fe56de7d6459?w=400&q=80"
                  alt="Gift Bouquet"
                  className="rounded-2xl w-full aspect-[4/3] object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1739131886213-7a9580cbc4e1?w=400&q=80"
                  alt="Festival Hamper"
                  className="rounded-2xl w-full aspect-square object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial / Trust Section */}
      <section className="section-spacing">
        <div className="container-handcraft text-center">
          <p className="font-handwritten text-2xl text-accent mb-4">Loved by many</p>
          <blockquote className="font-serif text-2xl md:text-4xl text-charcoal max-w-4xl mx-auto leading-relaxed mb-8">
            "The attention to detail in every piece is remarkable. My custom portrait exceeded all expectations.
            Truly a treasure I'll cherish forever."
          </blockquote>
          <p className="text-muted-foreground">— Happy Customer</p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
